import { css } from "styled-components";
import { Theme, Variant } from "./shared.types";

  
type ThemeAndVariantProps = { theme: Theme, variant: Variant };

// https://coolors.co/fec307
// Mikado Yellow
export const colorPrimary = "#FEC307";

// A shade is created by adding black to a base color,
// increasing its darkness. Shades appear more
// dramatic and richer.
export const colorPrimaryShade1 = "#F0B901";
export const colorPrimaryShade2 = "#DEAA01";
export const colorPrimaryShade3 = "#CB9C01";
export const colorPrimaryShade4 = "#B98E01";
export const colorPrimaryShade5 = "#A68001";
export const colorPrimaryShade6 = "#947201";
export const colorPrimaryShade7 = "#816301";
export const colorPrimaryShade8 = "#6F5501";
export const colorPrimaryShade9 = "#5C4700";
export const colorPrimaryShade10 = "#4A3900";
export const colorPrimaryShade11 = "#372B00";
export const colorPrimaryShade12 = "#251C00";
export const colorPrimaryShade13 = "#120E00";
export const colorPrimaryShade14 = "#000000";

// A tint is created by adding white to a base color,
// increasing its lightness. Tints are likely to look
// pastel and less intense.
export const colorPrimaryTint1 = "#FEC818";
export const colorPrimaryTint2 = "#FECC2A";
export const colorPrimaryTint3 = "#FED13C";
export const colorPrimaryTint4 = "#FED54D";
export const colorPrimaryTint5 = "#FED95F";
export const colorPrimaryTint6 = "#FEDD71";
export const colorPrimaryTint7 = "#FEE283";
export const colorPrimaryTint8 = "#FEE694";
export const colorPrimaryTint9 = "#FFEAA6";
export const colorPrimaryTint10 = "#FFEEB8";
export const colorPrimaryTint11 = "#FFF2CA";
export const colorPrimaryTint12 = "#FFF7DB";
export const colorPrimaryTint13 = "#FFFBED";
export const colorPrimaryTint14 = "#FFFFFF";

// Analogous color schemes are made by picking three colors
// that are next to each other on the color wheel.
// They are perceived as calm and serene.
export const colorPrimaryAnalogousLeft = "#FE1F06";
export const colorPrimaryAnalogousRight = "#93FE06";

// Complementary color schemes are made by picking two
// opposite colors con the color wheel. They appear vibrant
// near to each other.
export const colorPrimaryComplement = "#0640FE";

// Split complementary schemes are like complementary but
// they uses two adiacent colors of the complement. They
// are more flexible than complementary ones.
export const colorPrimarySplit1 = "#0693FE";
export const colorPrimarySplit2 = "#1F06FE";

// Triadic color schemes are created by picking three colors
// equally spaced on the color wheel. They appear quite
// contrasted and multicolored.
export const colorPrimaryTriadic1 = "#06FEC4";
export const colorPrimaryTriadic2 = "#C406FE";

// Tetradic color schemes are made form two couples of
// complementary colors in a rectangular shape on the color
// wheel. They are very versatile, and work best with one
// dominant color.
export const colorPrimaryTetradic1 = "#40FE06";
export const colorPrimaryTetradic2 = "#0640FE";
export const colorPrimaryTetradic3 = "#C406FE";

// Square color schemes are like tetradic arranged in a
// square instead of rectangle. Colors appear even
// more contrasting.
export const colorPrimarySquare1 = "#06FE48";
export const colorPrimarySquare2 = "#0640FE";
export const colorPrimarySquare3 = "#FE06BC";

// A hue refers to the basic family of a color from red
// to violet. Hues are variations of a base color on the
// color wheel.
export const colorPrimaryHueRight1 = "#FFF008";
export const colorPrimaryHueRight2 = "#E4FF08";
export const colorPrimaryHueRight3 = "#BAFF08";
export const colorPrimaryHueRight4 = "#8FFF08";
export const colorPrimaryHueRight5 = "#65FF08";
export const colorPrimaryHueRight6 = "#3AFF08";
export const colorPrimaryHueRight7 = "#10FF08";

export const colorPrimaryHueLeft1 = "#FF9B08";
export const colorPrimaryHueLeft2 = "#FF7008";
export const colorPrimaryHueLeft3 = "#FF4608";
export const colorPrimaryHueLeft4 = "#FF1C08";
export const colorPrimaryHueLeft5 = "#FF081E";
export const colorPrimaryHueLeft6 = "#FF0848";
export const colorPrimaryHueLeft7 = "#FF0873";

export const colorSecondary = "#36383d";

// A shade is created by adding black to a base color,
// increasing its darkness. Shades appear more
// dramatic and richer.
export const colorSecondaryShade1 = "#33353A";
export const colorSecondaryShade2 = "#2F3135";
export const colorSecondaryShade3 = "#2B2D31";
export const colorSecondaryShade4 = "#27292C";
export const colorSecondaryShade5 = "#232528";
export const colorSecondaryShade6 = "#202124";
export const colorSecondaryShade7 = "#1C1D1F";
export const colorSecondaryShade8 = "#18181B";
export const colorSecondaryShade9 = "#141416";
export const colorSecondaryShade10 = "#101012";
export const colorSecondaryShade11 = "#0C0C0D";
export const colorSecondaryShade12 = "#080809";
export const colorSecondaryShade13 = "#040404";
export const colorSecondaryShade14 = "#000000";

// A tint is created by adding white to a base color,
// increasing its lightness. Tints are likely to look
// pastel and less intense.
export const colorSecondaryTint1 = "#44474D";
export const colorSecondaryTint2 = "#51545C";
export const colorSecondaryTint3 = "#5F626B";
export const colorSecondaryTint4 = "#6C707A";
export const colorSecondaryTint5 = "#797D88";
export const colorSecondaryTint6 = "#888C96";
export const colorSecondaryTint7 = "#979AA3";
export const colorSecondaryTint8 = "#A6A9B0";
export const colorSecondaryTint9 = "#B5B7BD";
export const colorSecondaryTint10 = "#C4C5CA";
export const colorSecondaryTint11 = "#D2D4D7";
export const colorSecondaryTint12 = "#E1E2E5";
export const colorSecondaryTint13 = "#F0F1F2";
export const colorSecondaryTint14 = "#FFFFFF";

export const colorSuccess = "#34b632"; // color-primary-hue-right-3;
export const colorDanger = "#ff6926"; // color-primary-analogous-left;
export const colorInfo = "#097aff"; // color-primary-split1;
export const colorMuted = colorSecondaryTint12;
export const colorWhite = colorPrimaryTint14;
export const colorDisabled = colorPrimaryTint14;




export const gamesheetColours = {
  surface: {
    defaultPageBackground: '#FAFAFA',
    cardAndFormInputs: '#FFFFFF',
    ctaButtons: '#FFC307',
    flowBackCancel: '#36383D',
    disabled: '#DBDBD7',
  },
  primary: {
    50: '#fff9e6',
    100: '#fff3cd',
    200: '#ffe79c',
    300: '#fedb6a',
    400: '#fecf39',
    500: '#fec307',
    600: '#cb9c06',
    700: '#987504',
    800: '#664e03',
    900: '#332701',
    950: '#000',
  },
  secondary: {
    50: '#d7d7d8',
    100: '#afafb1',
    200: '#86888b',
    300: '#8a8b86',
    400: '#5e6064',
    500: '#36383d',
    600: '#2b2d31',
    700: '#202225',
    800: '#161618',
    900: '#0b0b0c',
    950: '#000',
  },
  ash: {
    50: '#FFFFFF',
    100: '#FDFDFC',
    200: '#FAFAFA',
    300: '#FAFAFA',
    400: '#F8F8F7',
    500: '#F5F5F4',
    600: '#C7C7C2',
    700: '#99998F',
    800: '#66665C',
    900: '#33332E',
    950: '#1B1B18'
  },
  liveBlue: {
    50: '#edf9ff',
    100: '#D7efff',
    200: '#b9e4ff',
    300: '#88d5ff',
    400: '#50bdff',
    500: '#289cff',
    600: '#097aff',
    700: '#0a66eb',
    800: '#0f52be',
    900: '#134895',
    950: '#112d5a',
  },
  successPalette: {
    50: '#f2fcf1',
    100: '#e0f9df',
    200: '#c2f2c0',
    300: '#92e590',
    400: '#5ad157',
    500: '#34b731',
    600: '#269623',
    700: '#21761f',
    800: '#1d5e1d',
    900: '#1a4d1a',
    950: '#092a0a',
  },
  error: {
    50: '#fbe9eb',
    100: '#f8d8d8',
    200: '#f1acb3',
    300: '#ea858f',
    400: '#e35e6b',
    500: '#dc3545',
    600: '#bb2030',
    700: '#8b1824',
    800: '#5b1017',
    900: '#30080c',
    950: '#160406',
  },
  orange: {
    50: '#FFF1E8',
    100: '#ffe3d6',
    200: '#ffc4a8',
    300: '#ffa880',
    400: '#ff8952',
    500: '#ff6926',
    600: '#eb4a00',
    700: '#b33900',
    800: '#752500',
    900: '#3d1300',
    950: '#1f0a00',
  },
};

export const primaryTheme: Theme = {
  primaryColor: "#FEC307", // existing primary color
  secondaryColor: colorSecondary, // existing secondary color
  textOnPrimaryColor: colorSecondary, // existing text on primary color
  textOnSecondaryColor: colorWhite, // existing text on secondary color
  success: colorSuccess, // existing success color
  danger:colorDanger, // existing danger color
  info: colorInfo, // existing info color
  muted: colorMuted, // existing muted color
  disabled:colorDisabled, // existing disabled color
  inverted: colorSecondary, // existing inverted color
  primary: gamesheetColours.primary,
  secondary: gamesheetColours.secondary,
  ash: gamesheetColours.ash,
  liveBlue: gamesheetColours.liveBlue,
  successPalette: gamesheetColours.successPalette,
  error: gamesheetColours.error,
  orange: gamesheetColours.orange,
};
export const invertedTheme: Theme = {
  primaryColor: colorSecondary,
  secondaryColor: colorPrimary,
  textOnPrimaryColor: colorPrimary,
  textOnSecondaryColor: colorSecondary,
  success: colorSuccess,
  danger: colorDanger,
  info: colorInfo,
  muted: colorMuted,
  disabled: colorDisabled,
  inverted: colorPrimary,
  primary: gamesheetColours.primary,
  secondary: gamesheetColours.secondary,
  ash: gamesheetColours.ash,
  liveBlue: gamesheetColours.liveBlue,
  successPalette: gamesheetColours.successPalette,
  error: gamesheetColours.error,
  orange: gamesheetColours.orange,
};

export const secondaryTheme: Theme = {
  primaryColor: colorSecondary,
  secondaryColor: colorMuted,
  textOnPrimaryColor: colorWhite,
  textOnSecondaryColor: colorSecondary,
  success: colorSuccess,
  danger: colorDanger,
  info: colorInfo,
  muted: colorMuted,
  disabled: colorDisabled,
  inverted: colorMuted,
  primary: gamesheetColours.primary,
  secondary: gamesheetColours.secondary,
  ash: gamesheetColours.ash,
  liveBlue: gamesheetColours.liveBlue,
  successPalette: gamesheetColours.successPalette,
  error: gamesheetColours.error,
  orange: gamesheetColours.orange,
};

export const Themes = {
  primary: primaryTheme,
  inverted: invertedTheme,
  secondary: secondaryTheme,
};

export const hex2rgba = (hex: string, alpha: number) => {
  //https://stackoverflow.com/questions/21646738/convert-hex-to-rgba
  // This turns a hex value into rgb
  // This is needed to do shading on a hex color. 
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export const hex2rgbaForShadows = (hex: string, alpha: number) => {
  //https://stackoverflow.com/questions/21646738/convert-hex-to-rgba
  // This turns a hex value into rgb
  // This is needed to do shading on a hex color. 
  if(hex.includes('#') && hex.length === 7){
    const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
    return `0px 0px 0px 3px rgba(${r},${g},${b},${alpha})`;
  }
};

export const textColorMixin = css<ThemeAndVariantProps>`
  color: ${(props) => 
    props.variant  &&  hex2rgba(props.theme[props.variant], 1) ||
    hex2rgba(props.theme.primaryColor, 1)
  };
` 

export const backgroundColorMutedMixin = css<ThemeAndVariantProps>`
  background-color: ${(props) => 
      props.variant &&  hex2rgba(props.theme[props.variant], 0.2) ||
      hex2rgba(props.theme.primaryColor, 0.2)
    };
`

export const borderColorMixing = css<ThemeAndVariantProps>`
  border: ${(props) => 
      props.variant &&  "1px solid" + props.theme[props.variant] ||
      "1px solid" + props.theme.primaryColor
    };
`


